import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, LogIn, PlugZap } from "lucide-react";
import {
  TOOLBOX_DOWNLOAD_PAGE_PATH,
  claudeSignInResultSchema,
  type HelpStatusState,
  type ToolboxBridge,
} from "@ttc-spectree/toolbox-contract";
import { plainBridgeError } from "@/lib/toolbox/bridge-error";
import { helpResetTime } from "@/lib/toolbox/help";
import { cn } from "@/lib/utils";

/**
 * Help when Claude cannot answer (epic PI-SPEC-SW-E302, feature
 * PI-SPEC-SW-1557, frame H5): say why in the same words as the Claude chip,
 * offer the one fix, and keep the Toolbox guide open without Claude. Never a
 * spinner. The card goes away by itself once the engine reports `ready`: when
 * Sign in to Claude returns signed in it asks the app again at once
 * (`onSignedIn`), and while the panel is open the bridge hook keeps asking
 * (SpecTree Windows walk 1, gap G-03).
 *
 * - signed out: Sign in to Claude (and Open Setup);
 * - Claude Code missing: Open Setup;
 * - usage limit: "Your Claude plan resets at 4:00 PM", no button;
 * - Help's files missing: get the latest version.
 */

/**
 * The engine status the card reads: Help's, or Product Q&A's (the same states;
 * Product Q&A's `message` is optional).
 */
export interface HelpNotReadyStatus {
  state: HelpStatusState;
  resetsAt?: string | undefined;
  message?: string | undefined;
}

/** `message` trimmed, or `fallback` when it is missing or blank. Pure; exported for tests. */
export function plainMessage(message: string | undefined, fallback: string): string {
  const trimmed = message?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : fallback;
}

/** The not-ready copy for one engine state: the card, the disabled ask box and the footer. */
export interface HelpNotReadyCopy {
  title: string;
  body: string;
  /** The ask box placeholder while disabled. */
  askReason: string;
  footer: string;
}

/** The words for a not-ready state. Pure; exported for tests. */
export function helpNotReadyCopy(status: HelpNotReadyStatus): HelpNotReadyCopy {
  const state: HelpStatusState = status.state;
  switch (state) {
    case "signed-out":
      return {
        title: "Help needs Claude to answer",
        body: "Claude is signed out on this computer, so Help cannot answer right now. Sign in again and Help is ready; nothing else needs to change.",
        askReason: "Help can answer again once Claude is signed in",
        footer:
          "Help opens the guide pages without Claude. Answers come back as soon as Claude is signed in.",
      };
    case "claude-missing":
      return {
        title: "Help needs Claude to answer",
        body: "Claude Code is not installed on this computer, so Help cannot answer right now. Setup installs it; then Help is ready.",
        askReason: "Help can answer again once Claude Code is installed",
        footer:
          "Help opens the guide pages without Claude. Answers come back as soon as Claude Code is installed.",
      };
    case "usage-limit": {
      const time = helpResetTime(status.resetsAt);
      return {
        title: "Help needs Claude to answer",
        body: time
          ? `Your Claude plan's usage limit is reached, so Help cannot answer right now. Your Claude plan resets at ${time}.`
          : "Your Claude plan's usage limit is reached, so Help cannot answer right now. Help answers again when your plan resets.",
        askReason: time
          ? `Help can answer again at ${time}`
          : "Help can answer again when your Claude plan resets",
        footer:
          "Help opens the guide pages without Claude. Answers come back as soon as your Claude plan resets.",
      };
    }
    case "plugin-missing":
      return {
        title: "Help's files are missing",
        body: "This copy of SpecTree Toolbox is missing the files Help reads, so Help cannot answer right now. Install the latest version to fix it.",
        askReason: "Help can answer again once the Toolbox is updated",
        footer:
          "Help opens the guide pages without Claude. Answers come back once the Toolbox is updated.",
      };
    default:
      return {
        title: "Help cannot answer right now",
        body: plainMessage(status.message, "Help cannot answer right now. Try again in a moment."),
        askReason: "Help can answer again once Claude is ready",
        footer:
          "Help opens the guide pages without Claude. Answers come back as soon as Claude is ready.",
      };
  }
}

const primaryButton =
  "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#E11837] px-2.5 text-xs font-semibold text-white hover:bg-[#C8102E] disabled:opacity-60";
const secondaryButton =
  "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[9px] text-xs font-medium text-[#FAFAFA] hover:bg-[#222226]";

/** The not-ready card (design `.glass` with the amber border). */
export function HelpNotReady({
  status,
  bridge,
  onOpenSetup,
  onSignedIn,
  copy: copyOverride,
  signInOpenedLine = "Claude's sign-in opened in your browser. Help is ready once you are signed in.",
}: {
  status: HelpNotReadyStatus;
  bridge: ToolboxBridge | null;
  onOpenSetup: () => void;
  /** Called when the Sign in to Claude flow returns (signed in, or the older open-only call): ask the app again. */
  onSignedIn?: () => void;
  /** Another tool's words for the card (Product Q&A, epic PI-SPEC-SW-E310); Help's by default. */
  copy?: HelpNotReadyCopy | undefined;
  /** The line after the older open-only sign-in call. Help's by default. */
  signInOpenedLine?: string;
}) {
  const copy = copyOverride ?? helpNotReadyCopy(status);
  const [busy, setBusy] = useState(false);
  const [line, setLine] = useState<string | null>(null);

  const signIn = async () => {
    if (!bridge) return;
    setBusy(true);
    setLine(null);
    try {
      if (typeof bridge.app.signInToClaude === "function") {
        const parsed = claudeSignInResultSchema.safeParse(await bridge.app.signInToClaude());
        if (!parsed.success || !parsed.data.ok) {
          const reason = parsed.success ? (parsed.data.reason?.trim() ?? "") : "";
          setLine(reason.length > 0 ? reason : "Claude's sign-in didn't finish. Try again.");
        } else {
          // Signed in: ask the app now instead of waiting for a reload (gap G-03).
          onSignedIn?.();
        }
      } else {
        await bridge.app.openClaudeSignIn();
        setLine(signInOpenedLine);
        onSignedIn?.();
      }
    } catch (error) {
      setLine(plainBridgeError(error, "Claude's sign-in didn't open. Try again in a moment."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-2.5 rounded-[10px] border border-[rgba(245,158,11,0.4)] bg-[#14110A] p-4"
      data-testid="help-not-ready"
      data-state={status.state}
    >
      <span className="flex items-center gap-2 font-display text-sm font-semibold leading-[1.3]">
        <PlugZap className="h-[18px] w-[18px] flex-none text-[#FBBF24]" aria-hidden="true" />
        {copy.title}
      </span>
      <span className="text-[13px] leading-[1.55] text-[#E4E4E7]">{copy.body}</span>
      {status.state === "signed-out" && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={primaryButton}
            disabled={busy}
            onClick={() => void signIn()}
          >
            <LogIn className="h-[13px] w-[13px]" aria-hidden="true" />
            Sign in to Claude
          </button>
          <button type="button" className={secondaryButton} onClick={onOpenSetup}>
            Open Setup
          </button>
        </div>
      )}
      {status.state === "claude-missing" && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={primaryButton} onClick={onOpenSetup}>
            Open Setup
          </button>
        </div>
      )}
      {status.state === "plugin-missing" && (
        <div className="flex flex-wrap gap-2">
          <Link to={TOOLBOX_DOWNLOAD_PAGE_PATH} className={primaryButton}>
            <Download className="h-[13px] w-[13px]" aria-hidden="true" />
            Get the latest version
          </Link>
        </div>
      )}
      {line && <span className={cn("text-xs leading-[1.55] text-[#FDE68A]")}>{line}</span>}
    </div>
  );
}
